// Sticky Add-To-Cart bar controller
// Responsibilities:
// - Visibility: show the sticky bar when main ATC leaves the viewport
// - Sync: keep subscribe/size selections in sync between PDP and sticky
// - ATC: forward sticky ATC clicks to the main ATC for consistent behavior
(function() {
  const stickyBar = document.getElementById('sticky-add-to-cart');
  if (!stickyBar) return;
  /** @type {HTMLElement & { _stickyIO?: IntersectionObserver }} */
  const stickyEl = /** @type {any} */(stickyBar);

  // =====================
  // Init + References
  // =====================
  initScrollDetection();
  const priceFinalEl = /** @type {HTMLElement|null} */(stickyBar.querySelector('.sticky-add-to-cart__price-final'));
  const priceOrigEl = /** @type {HTMLElement|null} */(stickyBar.querySelector('.sticky-add-to-cart__price-original'));
  const stickyBtn = /** @type {HTMLButtonElement|null} */(stickyBar.querySelector('.sticky-add-to-cart__button'));
  // Badge text will be read fresh from DOM when needed
  function sanitizeBadgeText(text) {
    const t = (text || '').trim();
    if (!t) return t;
    return t.split('+')[0].trim();
  }

  // Keep sticky button pointing to the same ATC URL as the main button
  /**
   * Mirror the main ATC href into sticky button dataset for fallback nav
   */
  function updateStickyAtcHref() {
    const mainAtc = /** @type {HTMLAnchorElement|null} */(document.querySelector('.product-showcase__add-to-cart'));
    const href = mainAtc?.getAttribute('href') || '';
    if (href && stickyBtn) stickyBtn.dataset.atcHref = href;
  }
  document.addEventListener('DOMContentLoaded', updateStickyAtcHref);
  document.addEventListener('pdp:product-changed', updateStickyAtcHref);
  document.addEventListener('pdp:option-selected', updateStickyAtcHref);

  // Keep sticky product titles in sync with main product title
  /**
   * Copy the current PDP title text into any sticky title placeholders
   */
  function updateStickyTitles() {
    const mainTitle = /** @type {HTMLElement|null} */(document.querySelector('.product-showcase__title'));
    const titleText = (mainTitle?.textContent || '').trim();
    if (!titleText) return;
    stickyBar.querySelectorAll('.sticky-add-to-cart__product-title').forEach((el) => {
      /** @type {HTMLElement} */(el).textContent = titleText;
    });
  }
  document.addEventListener('DOMContentLoaded', updateStickyTitles);
  document.addEventListener('pdp:product-changed', updateStickyTitles);


  // Keep sticky prices in sync with selected option
  /**
   * Update sticky cart prices based on selected option (subscription vs one-time)
   */
  function updateStickyPrices() {
    if (!priceFinalEl || !priceOrigEl) return;
    
    // Check which option is selected in sticky cart
    const selectedOption = stickyBar.querySelector('.sticky-add-to-cart__dropdown-item--selected');
    if (!selectedOption) {
      // No option selected yet, hide compare price by default
      priceOrigEl.style.display = 'none';
      return;
    }
    
    const selectedType = selectedOption.getAttribute('data-type') || 'subscription';
    
    // Get prices from the selected option in PDP
    let finalPrice = '';
    let comparePrice = '';
    let hasCompare = false;
    
    if (selectedType === 'onetime') {
      // One-time: no compare price, just the regular price
      const onetimeOption = document.querySelector('.product-showcase__subscribe--option[data-type="onetime"]');
      if (onetimeOption) {
        const priceEl = onetimeOption.querySelector('.product-showcase__subscribe--options--price');
        finalPrice = (priceEl?.textContent || '').trim();
      }
    } else if (selectedType === 'subscription-special') {
      // Starter kit subscription: compare = one-time price, final = subscription price
      const specialOption = document.querySelector('.product-showcase__subscribe--option[data-type="subscription-special"]');
      const onetimeOption = document.querySelector('.product-showcase__subscribe--option[data-type="onetime"]');
      
      if (specialOption) {
        const priceEl = specialOption.querySelector('.product-showcase__subscribe--options--price');
        finalPrice = (priceEl?.textContent || '').trim();
      }
      
      // Compare price is the one-time price
      if (onetimeOption) {
        const comparePriceEl = onetimeOption.querySelector('.product-showcase__subscribe--options--price');
        comparePrice = (comparePriceEl?.textContent || '').trim();
        hasCompare = !!comparePrice;
      }
      
      // Fallback: get compare price from PDP header
      if (!hasCompare) {
        const pdpCompare = document.querySelector('.product-showcase__compare-price');
        comparePrice = (pdpCompare?.textContent || '').trim();
        hasCompare = !!comparePrice;
      }
    } else if (selectedType === 'subscription') {
      // Regular subscription: compare = one-time price, final = subscription price
      const subsOption = document.querySelector('.product-showcase__subscribe--option[data-type="subscription"]');
      const onetimeOption = document.querySelector('.product-showcase__subscribe--option[data-type="onetime"]');
      
      if (subsOption) {
        const priceEl = subsOption.querySelector('.product-showcase__subscribe--options--price');
        finalPrice = (priceEl?.textContent || '').trim();
      }
      
      // Compare price is the one-time price
      if (onetimeOption) {
        const comparePriceEl = onetimeOption.querySelector('.product-showcase__subscribe--options--price');
        comparePrice = (comparePriceEl?.textContent || '').trim();
        hasCompare = !!comparePrice;
      }
      
      // Fallback: get compare price from PDP header
      if (!hasCompare) {
        const pdpCompare = document.querySelector('.product-showcase__compare-price');
        comparePrice = (pdpCompare?.textContent || '').trim();
        hasCompare = !!comparePrice;
      }
    }
    
    // Update sticky cart prices
    if (finalPrice) {
      priceFinalEl.textContent = finalPrice;
    }
    
    if (priceOrigEl) {
      if (hasCompare && comparePrice) {
        priceOrigEl.textContent = comparePrice;
        priceOrigEl.style.display = '';
      } else {
        priceOrigEl.textContent = '';
        priceOrigEl.style.display = 'none';
      }
    }
  }
  document.addEventListener('DOMContentLoaded', updateStickyPrices);
  document.addEventListener('pdp:product-changed', updateStickyPrices);

  // =====================
  // ATC Click delegation
  // =====================
  if (stickyBtn) {
    stickyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      
      // Determine which option is selected in sticky cart
      const selectedOption = stickyBar.querySelector('.sticky-add-to-cart__dropdown-item--selected');
      if (!selectedOption) return;
      
      const selectedType = selectedOption.getAttribute('data-type') || 'subscription';
      const selectedVariantId = selectedOption.getAttribute('data-variant-id') || '';
      const selectedPlanId = selectedOption.getAttribute('data-selling-plan-id') || '';
      
      // First, ensure the PDP has the correct option selected by triggering the selection event
      document.dispatchEvent(new CustomEvent('pdp:option-selected', { 
        detail: { 
          source: 'sticky', 
          type: selectedType, 
          planId: selectedPlanId, 
          variantId: selectedVariantId 
        } 
      }));
      
      // Wait a brief moment for the PDP to update, then click the correct button
      setTimeout(() => {
        /** @type {HTMLAnchorElement|null} */
        let mainAtc = null;
        
        if (selectedType === 'onetime') {
          // One-time purchase button
          mainAtc = /** @type {HTMLAnchorElement|null} */(document.querySelector('#ps-add-to-cart-onetime'));
        } else if (selectedType === 'subscription-special' && selectedVariantId) {
          // Subscription with starter kit (has variant-id)
          // Find the button in the subscription-special container
          const specialContainers = document.querySelectorAll('.product-showcase__subscribe--container');
          for (const container of specialContainers) {
            const option = container.querySelector('.product-showcase__subscribe--option[data-type="subscription-special"]');
            if (option && option.getAttribute('data-variant-id') === selectedVariantId) {
              mainAtc = /** @type {HTMLAnchorElement|null} */(container.querySelector('.product-showcase__add-to-cart'));
              break;
            }
          }
        } else if (selectedType === 'subscription') {
          // Regular subscription - find the container with matching plan ID
          const subscriptionContainers = document.querySelectorAll('.product-showcase__subscribe--container');
          for (const container of subscriptionContainers) {
            const option = container.querySelector('.product-showcase__subscribe--option[data-type="subscription"]');
            if (option && (!selectedPlanId || option.getAttribute('data-selling-plan-id') === selectedPlanId)) {
              mainAtc = /** @type {HTMLAnchorElement|null} */(container.querySelector('.product-showcase__add-to-cart'));
              break;
            }
          }
        }
        
        // Fallback: first available button
        if (!mainAtc) {
          mainAtc = /** @type {HTMLAnchorElement|null} */(document.querySelector('.product-showcase__add-to-cart'));
        }
        
        if (mainAtc) {
          const evt = new MouseEvent('click', { bubbles: true, cancelable: true });
          const clicked = mainAtc.dispatchEvent(evt);
          if (!clicked) return; // some handler consumed it (drawer, etc.)
          const href = mainAtc.getAttribute('href');
          if (href) window.location.assign(href);
          return;
        }
        
        // Fallback: navigate using last known href
        const href = stickyBtn.dataset.atcHref || '';
        if (href) window.location.assign(href);
      }, 50);
    });
  }

  // =====================
  // Event delegation: open/close dropdowns
  // =====================
  stickyBar.addEventListener('click', (e) => {
    const tgt = /** @type {Element|null} */(e.target instanceof Element ? e.target : null);
    if (!tgt) return;
    const toggle = tgt.closest('.sticky-add-to-cart__dropdown-toggle');
    if (!toggle) return;
    // If toggle is disabled (only one option), do nothing
    if (toggle.classList.contains('is-disabled')) return;
    e.preventDefault();
    e.stopPropagation();
    const dropdown = toggle.closest('.sticky-add-to-cart__dropdown');
    if (!dropdown) return;
    const wasOpen = dropdown.classList.contains('open');
    // Close all others first
    stickyBar.querySelectorAll('.sticky-add-to-cart__dropdown').forEach(dd => dd.classList.remove('open'));
    // If it was open, keep it closed (thus clicking the toggle closes it)
    if (wasOpen) {
      dropdown.classList.remove('open');
      return;
    }
    // Otherwise, open it
    dropdown.classList.add('open');
  });

  // =====================
  // Event delegation: select dropdown items (subscribe + size)
  // =====================
  stickyBar.addEventListener('click', (e) => {
    const tgt = /** @type {Element} */(e.target instanceof Element ? e.target : null);
    if (!tgt) return;
    const item = tgt.closest('.sticky-add-to-cart__dropdown-item, .sticky-add-to-cart__dropdown-variant');
    if (!item) return;
    e.preventDefault();
    e.stopPropagation();
    const parentMenu = item.closest('.sticky-add-to-cart__dropdown-menu');
    const dropdown = item.closest('.sticky-add-to-cart__dropdown');
    if (parentMenu) {
      parentMenu.querySelectorAll('.sticky-add-to-cart__dropdown-item, .sticky-add-to-cart__dropdown-variant').forEach(el => el.classList.remove('sticky-add-to-cart__dropdown-item--selected', 'sticky-add-to-cart__dropdown-variant--selected'));
      if (item.classList.contains('sticky-add-to-cart__dropdown-variant')) item.classList.add('sticky-add-to-cart__dropdown-variant--selected');
      else item.classList.add('sticky-add-to-cart__dropdown-item--selected');
    }
    if (dropdown) dropdown.classList.remove('open');

    // Identify which control group was interacted with
    const toggle = dropdown ? dropdown.querySelector('.sticky-add-to-cart__dropdown-toggle') : null;
    const group = toggle ? toggle.getAttribute('data-dropdown') || '' : '';

    // Broadcast selection so main product area can sync
    if (group === 'subscribe') {
      const label = (item.querySelector('.sticky-add-to-cart__dropdown-item-text')?.textContent || '').toLowerCase();
      const typeAttr = item.getAttribute('data-type');
      const type = typeAttr || (label.includes('one-time') ? 'onetime' : 'subscription');
      const planId = item.getAttribute('data-selling-plan-id') || '';
      const variantId = item.getAttribute('data-variant-id') || '';
      document.dispatchEvent(new CustomEvent('pdp:option-selected', { detail: { source: 'sticky', type, planId, variantId } }));
      // Update toggle label
      const labelSpan = toggle?.querySelector('.sticky-add-to-cart__dropdown-label');
      const txt = item.querySelector('.sticky-add-to-cart__dropdown-item-text')?.textContent || '';
      if (labelSpan) labelSpan.textContent = txt;
    } else if (group === 'size') {
      // Forward to main size selector via click on corresponding button by variant id if available
      const vid = item.getAttribute('data-variant-id');
      let forwarded = false;
      if (vid) {
        const btn = document.querySelector('.product-variants--size .variant-product__button[data-variant-id="' + CSS.escape(vid) + '"]');
        if (btn) {
          const evt = new Event('click', { bubbles: true, cancelable: true });
          btn.dispatchEvent(evt);
          forwarded = true;
        }
      }
      // If no normal size button, try bundle size card (by variant id or product handle)
      if (!forwarded) {
        const handle = item.getAttribute('data-product-handle') || '';
        let bBtn = null;
        if (vid) bBtn = document.querySelector('.product-variants--bundle .variant-product__button[data-variant-id="' + CSS.escape(vid) + '"]');
        if (!bBtn && handle) bBtn = document.querySelector('.product-variants--bundle .variant-product__button[data-product-handle="' + CSS.escape(handle) + '"]');
        if (bBtn) {
          const evt = new Event('click', { bubbles: true, cancelable: true });
          bBtn.dispatchEvent(evt);
          forwarded = true;
        }
      }
    }
  });

  // Close menus when clicking outside
  document.addEventListener('click', (e) => {
    const tgt = /** @type {Element|null} */(e.target instanceof Element ? e.target : null);
    if (!tgt) return;
    if (!tgt.closest('.sticky-add-to-cart__dropdown')) {
      stickyBar.querySelectorAll('.sticky-add-to-cart__dropdown').forEach(dd => dd.classList.remove('open'));
    }
  });

  function initScrollDetection() {
    /** @type {HTMLElement[]} */
    const atcButtons = Array.from(document.querySelectorAll('.product-showcase__add-to-cart'));
    if (!atcButtons.length) {
      console.warn('Add to cart buttons not found');
      return;
    }

    function updateStickyVisibility() {
      // Mostrar sticky cuando los botones han llegado o pasado el top del viewport
      const allAtOrAboveTop = atcButtons.every((btn) => {
        const rect = btn.getBoundingClientRect();
        return rect.top <= 0;
      });

      if (allAtOrAboveTop) {
        stickyBar.classList.add('show');
      } else {
        stickyBar.classList.remove('show');
        stickyBar.querySelectorAll('.sticky-add-to-cart__dropdown').forEach(dd => dd.classList.remove('open'));
      }
    }

    // --- IntersectionObserver para disparar cuando entran/salen del viewport
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(() => {
        updateStickyVisibility();
      }, { root: null, threshold: [0, 0.01, 1] });

      atcButtons.forEach(el => io.observe(el));
      stickyEl._stickyIO = io;
    }

    let ticking = false;
    function requestTick() {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateStickyVisibility();
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick, { passive: true });
    window.addEventListener('load', updateStickyVisibility);
    updateStickyVisibility();
  }

  // Listen for selection changes from main area and reflect in sticky controls
  document.addEventListener('pdp:option-selected', function(ev){
    const d = /** @type {{source?:string,type?:string,priceText?:string,compareText?:string,hasCompare?:boolean}} */((/** @type {CustomEvent} */(ev)).detail || {});
    if (!d || d.source === 'sticky') return;

    // Update subscribe dropdown selection and label
    const subscribeDropdown = stickyBar.querySelector('.sticky-add-to-cart__dropdown-toggle[data-dropdown="subscribe"]');
    const subscribeMenu = stickyBar.querySelector('.sticky-add-to-cart__dropdown-menu[data-dropdown-menu="subscribe"]');
    if (subscribeDropdown && subscribeMenu && d.type) {
      const items = subscribeMenu.querySelectorAll('.sticky-add-to-cart__dropdown-item');
      items.forEach(el => el.classList.remove('sticky-add-to-cart__dropdown-item--selected'));
      /** @type {HTMLElement|null} */
      let target = null;
      items.forEach(el => {
        const label = (el.querySelector('.sticky-add-to-cart__dropdown-item-text')?.textContent || '').toLowerCase();
        const typeAttr = el.getAttribute('data-type');
        const type = typeAttr || (label.includes('one-time') ? 'onetime' : 'subscription');
        if (!target && type === d.type) target = /** @type {HTMLElement} */(el);
      });
      if (target) {
        target.classList.add('sticky-add-to-cart__dropdown-item--selected');
        const labelSpan = subscribeDropdown.querySelector('.sticky-add-to-cart__dropdown-label');
        const targetText = target.querySelector('.sticky-add-to-cart__dropdown-item-text')?.textContent || '';
        if (labelSpan) labelSpan.textContent = targetText;
      }
    }

    // Update sticky prices to match main header
    if (priceFinalEl && d.priceText) {
      priceFinalEl.textContent = d.priceText;
    }
    if (priceOrigEl) {
      if (d.hasCompare && d.compareText) {
        priceOrigEl.textContent = d.compareText;
        priceOrigEl.style.display = '';
      } else {
        // hide when not applicable
        priceOrigEl.textContent = '';
        priceOrigEl.style.display = 'none';
      }
    }
    // Also refresh prices to reflect the new option/product
  });

  // Build sticky dropdown menus based on the main DOM so they stay in sync (subscribe + size)
  function syncFromMain() {
    // Rebuild subscribe list from ALL purchase options on the page so we include special blocks too
    const stickyMenu = stickyBar.querySelector('.sticky-add-to-cart__dropdown-menu[data-dropdown-menu="subscribe"]');
    if (stickyMenu) {
      const frag = document.createDocumentFragment();
      document.querySelectorAll('.product-showcase__subscribe--option').forEach((optEl) => {
        const item = document.createElement('div');
        item.className = 'sticky-add-to-cart__dropdown-item' + (optEl.classList.contains('is-selected') ? ' sticky-add-to-cart__dropdown-item--selected' : '');
        const type = optEl.getAttribute('data-type') || '';
        const planId = optEl.getAttribute('data-selling-plan-id') || '';
        const variantId = optEl.getAttribute('data-variant-id') || '';
        const title = optEl.querySelector('.product-showcase__subscribe--options--title');
        const titleText = title ? title.childNodes ? Array.from(title.childNodes).filter(n=>n.nodeType===Node.TEXT_NODE).map(n=>n.textContent||'').join('').trim() : (title.textContent||'').trim() : '';
        const priceText = optEl.getAttribute('data-price-text') || (optEl.querySelector('.product-showcase__subscribe--options--price')?.textContent || '').trim();
        item.setAttribute('data-type', type);
        if (planId) item.setAttribute('data-selling-plan-id', planId);
        if (variantId) item.setAttribute('data-variant-id', variantId);
        item.innerHTML = '<span class="sticky-add-to-cart__dropdown-item-text"></span><span class="sticky-add-to-cart__dropdown-price"></span>';
        const tSpan = item.querySelector('.sticky-add-to-cart__dropdown-item-text');
        const pSpan = item.querySelector('.sticky-add-to-cart__dropdown-price');
        if (tSpan) tSpan.textContent = titleText || (type==='subscription' ? 'Subscribe & Save' : 'One-Time Purchase');
        if (pSpan) pSpan.textContent = priceText;
        const badge = optEl.querySelector('.product-showcase__subscribe--options--title--save');
        if (badge && (badge.textContent||'').trim()) {
          const badgeWrap = document.createElement('div');
          badgeWrap.className = 'sticky-add-to-cart__dropdown-discount-container';
          badgeWrap.innerHTML = '<span class="sticky-add-to-cart__dropdown-discount"></span>';
          const b = badgeWrap.querySelector('.sticky-add-to-cart__dropdown-discount');
          if (b) b.textContent = sanitizeBadgeText((badge.textContent||'').trim());
          item.appendChild(badgeWrap);
        }
        frag.appendChild(item);
      });
      stickyMenu.innerHTML = '';
      stickyMenu.appendChild(frag);
      // Update current label
      const selected = stickyMenu.querySelector('.sticky-add-to-cart__dropdown-item--selected') || stickyMenu.querySelector('.sticky-add-to-cart__dropdown-item');
      const toggle = stickyBar.querySelector('.sticky-add-to-cart__dropdown-toggle[data-dropdown="subscribe"] .sticky-add-to-cart__dropdown-label');
      if (selected && toggle) toggle.textContent = selected.querySelector('.sticky-add-to-cart__dropdown-item-text')?.textContent || '';
      
      // Enable/disable dropdown based on total number of options (including one-time)
      const subscribeToggle = /** @type {HTMLElement|null} */(stickyBar.querySelector('.sticky-add-to-cart__dropdown-toggle[data-dropdown="subscribe"]'));
      if (subscribeToggle) {
        const allItems = stickyMenu.querySelectorAll('.sticky-add-to-cart__dropdown-item');
        const totalOptions = allItems.length;
        const onlyOneOption = totalOptions <= 1;
        
        // Disable dropdown only if there's 1 or fewer total options
        subscribeToggle.classList.toggle('is-disabled', onlyOneOption);
        
        // Hide chevron icon when disabled
        const icon = /** @type {HTMLElement|null} */(subscribeToggle.querySelector('.sticky-add-to-cart__dropdown-icon'));
        if (icon) icon.style.visibility = onlyOneOption ? 'hidden' : '';
      }
    }

    // Rebuild size list from .product-variants--size or fallback to bundle cards (include image, servings, weight)
    const mainSize = document.querySelector('.product-variants--size .variant-products');
    const bundleList = document.querySelector('.product-variants--bundle .variant-products');
    const stickySizeMenu = stickyBar.querySelector('.sticky-add-to-cart__dropdown-menu[data-dropdown-menu="size"]');
    const sizeToggle = stickyBar.querySelector('.sticky-add-to-cart__dropdown-toggle[data-dropdown="size"]');
    if (stickySizeMenu) {
      const frag = document.createDocumentFragment();
      let btns = mainSize ? Array.from(mainSize.querySelectorAll('.variant-product__button')) : [];
      let fromBundle = false;
      if (!btns.length && bundleList) {
        btns = Array.from(bundleList.querySelectorAll('.variant-product__button'));
        fromBundle = true;
      }
      const count = btns.length;
      /**
       * Create a size/bundle item for the sticky menu reflecting the PDP card
       * @param {HTMLElement} btn
       * @param {boolean} selected
       */
      const buildItem = (btn, selected) => {
        const li = btn.closest('.variant-product');
        const item = document.createElement('div');
        item.className = 'sticky-add-to-cart__dropdown-variant' + (selected ? ' sticky-add-to-cart__dropdown-variant--selected' : '');
        const vid = btn.getAttribute('data-variant-id') || '';
        const val = btn.getAttribute('data-option-value') || '';
        item.setAttribute('data-variant-id', vid);
        item.setAttribute('data-value', val);
        const handle = btn.getAttribute('data-product-handle') || '';
        if (handle) item.setAttribute('data-product-handle', handle);
        
        // Get title from PDP card
        const title = li?.querySelector('.variant-product__title')?.textContent?.trim() || val || '';
        
        // Get price from PDP card
        const priceEl = li?.querySelector('.variant-product__price');
        const price = priceEl?.textContent?.trim() || '';
        
        // Get discount/badge from PDP card
        const badgeEl = li?.querySelector('.variant-product__badge, .variant-product__discount');
        const discount = badgeEl?.textContent?.trim() || '';
        
        // Get servings and weight
        const servingsEl = li?.querySelector('.variant-product__servings-text');
        const servings = servingsEl?.textContent?.trim() || '';
        const weightEl = li?.querySelector('.variant-product__servings-number');
        const weight = weightEl?.textContent?.trim() || '';
        
        // Build full info block
        item.innerHTML = '' +
          '<div class="sticky-add-to-cart__dropdown-variant-info">' +
            '<span class="sticky-add-to-cart__dropdown-variant-title"></span>' +
            (price ? '<span class="sticky-add-to-cart__dropdown-price"></span>' : '') +
            (discount ? '<span class="sticky-add-to-cart__dropdown-discount"></span>' : '') +
            (servings ? '<span class="sticky-add-to-cart__dropdown-servings"></span>' : '') +
            (weight ? '<span class="sticky-add-to-cart__dropdown-weight"></span>' : '') +
          '</div>';
        
        const t = item.querySelector('.sticky-add-to-cart__dropdown-variant-title');
        if (t) t.textContent = title;
        const pEl = item.querySelector('.sticky-add-to-cart__dropdown-price');
        if (pEl && price) pEl.textContent = price;
        const dEl = item.querySelector('.sticky-add-to-cart__dropdown-discount');
        if (dEl && discount) dEl.textContent = sanitizeBadgeText(discount);
        const sEl = item.querySelector('.sticky-add-to-cart__dropdown-servings');
        if (sEl && servings) sEl.textContent = servings;
        const wEl = item.querySelector('.sticky-add-to-cart__dropdown-weight');
        if (wEl && weight) wEl.textContent = weight;
        
        frag.appendChild(item);
      };
      if (count > 0) {
        btns.forEach((btn) => {
          const li = btn.closest('.variant-product');
          // For bundle we also use the selected/active classes we set from PDP
          const isSel = li ? (li.classList.contains('variant-product-selected') || li.classList.contains('variant-product--active')) : false;
          buildItem(btn, isSel);
        });
      } else {
        // No size block present: keep a single option using current variant title
        const selectedLabel = document.querySelector('.product-variants__selected');
        const currentTitle = (selectedLabel?.textContent || '').trim() || 'Default';
        const dummy = document.createElement('div');
        dummy.className = 'sticky-add-to-cart__dropdown-variant sticky-add-to-cart__dropdown-variant--selected';
        dummy.setAttribute('data-variant-id', '');
        dummy.setAttribute('data-value', currentTitle);
        dummy.innerHTML = '<div class="sticky-add-to-cart__dropdown-variant-info"><span class="sticky-add-to-cart__dropdown-variant-title"></span></div>';
        const t = dummy.querySelector('.sticky-add-to-cart__dropdown-variant-title');
        if (t) t.textContent = currentTitle;
        frag.appendChild(dummy);
      }
      stickySizeMenu.innerHTML = '';
      stickySizeMenu.appendChild(frag);
      // Update current size label
      const selected = stickySizeMenu.querySelector('.sticky-add-to-cart__dropdown-variant--selected') || stickySizeMenu.querySelector('.sticky-add-to-cart__dropdown-variant');
      const toggleLabel = stickyBar.querySelector('.sticky-add-to-cart__dropdown-toggle[data-dropdown="size"] .sticky-add-to-cart__dropdown-label');
      if (selected && toggleLabel) toggleLabel.textContent = selected.querySelector('.sticky-add-to-cart__dropdown-variant-title')?.textContent || 'SIZE';
      // If there is only one variant, disable dropdown interaction
      const onlyOne = stickySizeMenu.querySelectorAll('.sticky-add-to-cart__dropdown-variant').length <= 1;
      if (sizeToggle) {
        if (onlyOne) sizeToggle.classList.add('is-disabled'); else sizeToggle.classList.remove('is-disabled');
      }

      // Hide only this SIZE option container (grandparent) when disabled, without touching other styles
      const sizeOptionContainer = /** @type {HTMLElement|null} */(sizeToggle ? sizeToggle.closest('.sticky-add-to-cart__option') : null);
      if (sizeOptionContainer) {
        if (onlyOne) {
          sizeOptionContainer.style.display = 'none';
          sizeOptionContainer.setAttribute('aria-hidden', 'true');
        } else {
          sizeOptionContainer.style.display = '';
          sizeOptionContainer.removeAttribute('aria-hidden');
        }
      }
    }
    // Mirror behavior for bundle-size if present
    const bundleToggle = /** @type {HTMLElement|null} */(stickyBar.querySelector('.sticky-add-to-cart__dropdown-toggle[data-dropdown="bundle-size"]'));
    const bundleMenu = /** @type {HTMLElement|null} */(stickyBar.querySelector('.sticky-add-to-cart__dropdown[data-dropdown="bundle-size"] .sticky-add-to-cart__dropdown-menu'));
    if (bundleToggle && bundleMenu) {
      const itemsCount = bundleMenu.querySelectorAll('.sticky-add-to-cart__dropdown-variant, .sticky-add-to-cart__dropdown-item').length;
      const onlyOneBundle = itemsCount <= 1;
      bundleToggle.classList.toggle('is-disabled', onlyOneBundle);
      const bundleOptionContainer = /** @type {HTMLElement|null} */(bundleToggle.closest('.sticky-add-to-cart__option'));
      if (bundleOptionContainer) {
        if (onlyOneBundle) {
          bundleOptionContainer.style.display = 'none';
          bundleOptionContainer.setAttribute('aria-hidden', 'true');
        } else {
          bundleOptionContainer.style.display = '';
          bundleOptionContainer.removeAttribute('aria-hidden');
        }
      }
    }
  }

  // Initial sync and on product/variant change
  document.addEventListener('DOMContentLoaded', () => {
    syncFromMain();
    // Also sync after a short delay to ensure all DOM is ready
    setTimeout(syncFromMain, 100);
  });

  // Optional direct hook: allow other modules to set the sticky badge text explicitly
  document.addEventListener('sticky:badge-text', (ev) => {
    const detail = /** @type {{text?: string, hide?: boolean}} */(/** @type {CustomEvent} */(ev).detail || {});
    const el = /** @type {HTMLElement|null} */(stickyBar.querySelector('.sticky-add-to-cart__product-percentage'));
    if (!el) return;
    if (detail && detail.hide) {
      el.textContent = '';
      el.style.display = 'none';
      return;
    }
    const txt = (detail && detail.text ? String(detail.text) : '').trim();
    if (txt) {
      el.textContent = sanitizeBadgeText(txt);
      el.style.display = '';
    }
  });
  
  document.addEventListener('pdp:product-changed', () => {
    // Rebuild menus and re-init visibility observer in case DOM changed
    syncFromMain();
    try {
      if (stickyEl._stickyIO && stickyEl._stickyIO.disconnect) stickyEl._stickyIO.disconnect();
    } catch {}
    initScrollDetection();
  });
  
  // Also sync when variant changes (size selection)
  document.addEventListener('pdp:variant-changed', () => {
    syncFromMain();
  });
})();
